type TransportClock = {
  date: string | null;
  time: string | null;
};

/** 无显式时区的行程时刻按日历字段解析，避免浏览器夏令时改变分钟差。 */
function transportTimestamp(value: string): number {
  const matched = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
  );
  if (!matched) return Date.parse(value);
  const [, year, month, day, hour, minute, second = "0", millisecond = "0"] = matched;
  return Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number(millisecond.padEnd(3, "0")),
  );
}

/** 完整时刻存在且递增时，以实际分钟差作为路线时长事实。 */
export function calculatedTransportDurationMinutes(
  departureAt?: string | null,
  arrivalAt?: string | null,
): number | null {
  if (!departureAt || !arrivalAt) return null;
  const departure = transportTimestamp(departureAt);
  const arrival = transportTimestamp(arrivalAt);
  if (!Number.isFinite(departure) || !Number.isFinite(arrival) || arrival <= departure) return null;
  return Math.round((arrival - departure) / 60_000);
}

/** 客户输出沿用紧凑时长，编辑节点满一小时后显示完整的小时与分钟。 */
export function formatTransportDuration(
  minutes?: number | null,
  format: "compact" | "hours-minutes" = "compact",
): string {
  if (minutes === null || minutes === undefined) return "";
  const normalized = Math.max(0, Math.round(minutes));
  if (normalized < (format === "hours-minutes" ? 60 : 120)) return `${normalized} 分钟`;
  const hours = Math.floor(normalized / 60);
  const remainder = normalized % 60;
  if (format === "hours-minutes") return `${hours} 小时 ${remainder} 分钟`;
  return `${hours}小时${remainder ? `${remainder}分` : ""}`;
}

/** 格式化交通时刻，并在到达日期晚于出发日期时明确标注跨日。 */
export function formatTransportTimeRange(
  departure?: string | null,
  arrival?: string | null,
): string {
  const start = transportClock(departure);
  const end = transportClock(arrival);
  if (!start.time && !end.time) return "";
  if (!start.time) return `到达 ${end.time}`;
  if (!end.time) return `出发 ${start.time}`;
  if (isSameDisplayedMinute(start, end)) return "";
  const offset = dayOffset(start.date, end.date);
  const arrivalLabel = offset === 1
    ? `次日 ${end.time}`
    : offset !== null && offset > 1
      ? `${offset}天后 ${end.time}`
      : end.time;
  return `${start.time}-${arrivalLabel}`;
}

/** 同一天内起止分钟完全相同通常是后端补位值，不应出现在客户输出中。 */
function isSameDisplayedMinute(start: TransportClock, end: TransportClock): boolean {
  if (start.time !== end.time) return false;
  if (start.date && end.date) return start.date === end.date;
  return !start.date && !end.date;
}

/** 提取 ISO 本地日期与分钟；纯时间输入仍可正常展示。 */
function transportClock(value?: string | null): TransportClock {
  if (!value) return { date: null, time: null };
  const dateTime = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (dateTime) return { date: dateTime[1], time: dateTime[2] };
  return { date: null, time: value.match(/^(\d{2}:\d{2})/)?.[1] ?? null };
}

/** 按日历日期计算非负天数差，避免浏览器时区改变显示。 */
function dayOffset(startDate: string | null, endDate: string | null): number | null {
  if (!startDate || !endDate) return null;
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.round((end - start) / 86_400_000);
}
